"use client";

/**
 * The product-card title link, shared by the desktop grid card and the mobile
 * list row.
 *
 * The two cards had byte-identical copies of this: same `href`, the same
 * ~18-line modifier-click handler, the same `aria-label`/`title`/`prefetch`, the
 * same emoji-stripped text — differing ONLY in the `className`. That handler is
 * the piece that mattered: it encodes which clicks open the in-page drawer and
 * which are allowed to navigate, and a divergence between the two copies would
 * mean the same click behaved differently on phone and desktop.
 *
 * `className` stays a required prop rather than a `size` variant because the two
 * strings are not two sizes of one idea — the desktop one carries a 44px touch
 * floor plus a `-mb` correction that the mobile row deliberately does not want
 * (see the comments at each call site).
 */

import Link from "next/link";
import { productPath } from "@/lib/product-url";
import type { Product } from "@/stores/product-store";
import { stripEmoji } from "../product-card-utils";

export function ProductCardTitleLink({
  product,
  onOpen,
  className,
}: {
  product: Product;
  /** Open the in-page detail drawer. */
  onOpen: () => void;
  className: string;
}) {
  return (
    <Link
      href={productPath(product)}
      // Plain-click opens the in-page drawer (much faster than a
      // full navigation, and preserves filter state in the URL).
      // Modifier-clicks (⌘/⊞/Ctrl, middle mouse, "open in new
      // tab" from a long-press) skip preventDefault and follow
      // the href to /p/[id]. Search crawlers also see the href.
      onClick={(e) => {
        if (
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey ||
          (e.nativeEvent as MouseEvent & { button?: number }).button === 1
        ) {
          return;
        }
        // Mouse clicks on the title still bubble to the card's onClick;
        // stop here so the drawer doesn't fire twice. Keyboard users
        // reach this link via Tab and activate via Enter.
        e.preventDefault();
        e.stopPropagation();
        onOpen();
      }}
      className={className}
      aria-label={`View ${stripEmoji(product.title)} details`}
      title={stripEmoji(product.title)}
      // Default Next prefetch on hover would prefetch every
      // product page in the viewport, which is wasteful. Disable
      // prefetch — the drawer doesn't navigate, and the rare
      // crawler / middle-click follower doesn't need a prefetch.
      prefetch={false}
    >
      {stripEmoji(product.title)}
    </Link>
  );
}
